import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Anchor, ArrowLeft, Compass, Download, Loader2, Sailboat,
  Scroll, Ship, Users, MessageSquareText, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { parseFollowup, stripFollowup } from "@/lib/followup";
import FollowUpSheet from "@/components/FollowUpSheet";
import FavoriteButton from "@/components/FavoriteButton";
import AiGeneratedBadge from "@/components/AiGeneratedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_AVATARS = {
  routeur: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/8a32cad401844db2c660f0152ac9e85b5847bbde3ca554477ea01904f8465f9b.png",
  analyse: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/19a86bf97520c7466c87f96c95a7fb5682566a3bb16f679be5d814a1aaebd419.png",
  strategie: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/a847752862b35df5e195ab26f0a225f02d9e2ee6159f7a7a1e53694a20a7cdab.png",
  nego: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4889000b20dcadf5cae0644c4005446f0f2df56d3e42f67d7c61bd19baef64d8.png",
  banque: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4348a57bda3ad71e0c15bfa2f66c4feb4891a609193bab30cbe74908226f41a9.png",
  travaux: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/27f670b872ef630033ea86eddb35f1a5cbbfba487ba70b64919bec800d6785d1.png",
  fiscalite: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/79686ac1613549b6d0093e4da9dec98ef8ee543744a38527a4d24646f507da62.png",
  locataire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/cd83e63bc00b54b2729dfa7347ffac350c80bf94cfe84c6c2f1955c58acd9faa.png",
  marche: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4eeb12fb01e2fa4178fa72384eafe29396c02d2fe9477f7d1174207db4a453ba.png",
  juridique: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/b9295ba9fe52e4da445b0eb8e245b1ee8f3c5ca69956a48976b72ab78714f30a.png",
  notaire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/0e489a0250e58a57ee0224da1c1e4af8d80ee0b8af54036914406cf159d489f6.png",
  chasse: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4eeb12fb01e2fa4178fa72384eafe29396c02d2fe9477f7d1174207db4a453ba.png",
};

const EXPERT_LABEL = {
  analyse: "ODYS'ANALYSE",
  strategie: "ODYS'STRATEGIE",
  banque: "ODYS'BANQUE",
  fiscalite: "ODYS'FISCALITE",
  notaire: "ODYS'NOTAIRE",
  juridique: "ODYS'JURIDIQUE",
  nego: "ODYS'NEGO",
  travaux: "ODYS'TRAVAUX",
  locataire: "ODYS'LOCATAIRE",
  marche: "ODYS'MARCHE",
  chasse: "ODYS'CHASSE",
};

const SAMPLE_QUESTIONS = [
  "Je vise un T2 a 180k EUR loue 800 EUR. Bon investissement avec mon TMI a 30 pourcent ?",
  "Faut-il que je passe en LMNP ou je reste en location nue pour mon premier bien ?",
  "Comment negocier un bien dans un marche tendu type Annecy ?",
  "150k EUR de cash, deja proprietaire de ma RP. Quelle strategie pour les 5 prochaines annees ?",
];

// Render markdown-like content (paragraphs + bullets + numeric lists)
function renderContent(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      out.push(
        <p key={`p-${out.length}`} className="tr-paragraph">
          {buf.join(" ")}
        </p>
      );
      buf = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) {
      flush();
      return;
    }
    if (/^[-•*]\s+/.test(line)) {
      flush();
      out.push(
        <div key={`b-${i}`} className="tr-bullet">
          <span className="tr-bullet-dot" />
          <span>{line.replace(/^[-•*]\s+/, "")}</span>
        </div>
      );
      return;
    }
    if (/^\d+[.)]\s+/.test(line)) {
      flush();
      const num = line.match(/^(\d+)[.)]/)[1];
      const rest = line.replace(/^\d+[.)]\s+/, "");
      out.push(
        <div key={`n-${i}`} className="tr-numbered">
          <span className="tr-numbered-idx">{num}.</span>
          <span>{rest}</span>
        </div>
      );
      return;
    }
    const upper = line.replace(":", "").trim().toUpperCase();
    if (
      upper.startsWith("VERDICT ODYS") ||
      upper.startsWith("LA RECO") ||
      upper.startsWith("POURQUOI") ||
      upper.startsWith("POINT DE VIGILANCE") ||
      upper.startsWith("PLAN") ||
      upper.startsWith("PROCHAINE ACTION")
    ) {
      flush();
      out.push(
        <div key={`h-${i}`} className="tr-section-label">
          {line}
        </div>
      );
      return;
    }
    buf.push(line);
  });
  flush();
  return out;
}

export default function TableRondePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const [initialExperts, setInitialExperts] = useState([]);
  const [interventions, setInterventions] = useState([]); // [{agent_id, name, content, streaming, appel}]
  const [arbiter, setArbiter] = useState({ active: false, content: "", done: false });
  const [roundtableId, setRoundtableId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [refinedVerdict, setRefinedVerdict] = useState({ active: false, content: "", done: false });
  const [refining, setRefining] = useState(false);

  // Smoothing : buffer texte + drain timer pour effet machine a ecrire fluide
  // target = { kind: 'expert'|'arbiter', index?: number }
  const streamRef = useRef({
    buffer: "",
    target: null,
    timer: null,
    interventionContents: [], // shadow copy pour eviter les races
    arbiterContent: "",
    lastTokenAt: 0,           // timestamp du dernier token recu (watchdog stall)
    watchdog: null,           // timer qui detecte si plus aucun token n'arrive
    rtIdRef: null,            // id de la roundtable courante (pour bascule polling)
    onStall: null,            // callback declenche quand watchdog detecte un stall
  });

  const debateEndRef = useRef(null);
  const ctrlRef = useRef(null);

  useEffect(() => {
    if (debateEndRef.current) {
      debateEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [interventions.length, arbiter.active, arbiter.done]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current.timer) {
        clearInterval(streamRef.current.timer);
      }
      if (streamRef.current.watchdog) {
        clearInterval(streamRef.current.watchdog);
      }
      if (ctrlRef.current) {
        try { ctrlRef.current.abort(); } catch {}
      }
    };
  }, []);

  // Chargement d'une Table Ronde existante via ?id=X (depuis Favoris ou Historique)
  useEffect(() => {
    const rtId = searchParams.get("id");
    if (!rtId) return;
    const token = localStorage.getItem("odys_token");
    (async () => {
      try {
        const res = await fetch(`${API}/roundtable/${rtId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          toast.error("Table Ronde introuvable.");
          return;
        }
        const rt = await res.json();
        setRoundtableId(rt.roundtable_id);
        setQuestion(rt.question || "");
        setInitialExperts(rt.initial_experts || []);
        const its = (rt.interventions || []).map((it) => ({
          agent_id: it.expert_id,
          name: it.expert_name,
          content: it.content,
          streaming: false,
          appel: null,
        }));
        setInterventions(its);
        const verdict = rt.refined_arbiter || rt.arbiter || "";
        if (verdict) {
          setArbiter({ active: true, content: verdict, done: true });
        }
        if (rt.refined_arbiter) {
          setRefinedVerdict({ active: true, content: rt.refined_arbiter, done: true });
        }
        setRunning(false);
      } catch (err) {
        console.warn("Load roundtable error:", err);
        toast.error("Erreur de chargement de la Table Ronde.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Watchdog : si aucun token recu en 35s, on considere le stream "stalle"
  // (cas observe en prod : Claude bloque silencieusement, le proxy continue d'envoyer
  // des keepalives, le front reste bloque sur "L'equipage est en seance" indefiniment).
  // Sur stall : on coupe la SSE et on bascule vers le polling DB.
  const TOKEN_STALL_MS = 35000;

  const startWatchdog = useCallback(() => {
    const s = streamRef.current;
    s.lastTokenAt = Date.now();
    if (s.watchdog) clearInterval(s.watchdog);
    s.watchdog = setInterval(() => {
      const ss = streamRef.current;
      if (!ss.lastTokenAt) return;
      if (Date.now() - ss.lastTokenAt > TOKEN_STALL_MS) {
        console.warn("[Roundtable] Stall detecte (>35s sans token). Bascule polling.");
        clearInterval(ss.watchdog);
        ss.watchdog = null;
        if (typeof ss.onStall === "function") {
          try { ss.onStall(); } catch {}
        }
      }
    }, 5000);
  }, []);

  const stopWatchdog = useCallback(() => {
    const s = streamRef.current;
    if (s.watchdog) {
      clearInterval(s.watchdog);
      s.watchdog = null;
    }
    s.lastTokenAt = 0;
  }, []);

  const pingToken = useCallback(() => {
    streamRef.current.lastTokenAt = Date.now();
  }, []);

  const drainStep = useCallback(() => {
    const s = streamRef.current;
    const remaining = s.buffer;
    if (!remaining || !s.target) {
      return;
    }
    const len = remaining.length;
    const chunkSize = len > 400 ? 5 : len > 150 ? 2 : 1;
    const chunk = remaining.slice(0, chunkSize);
    s.buffer = remaining.slice(chunkSize);

    if (s.target.kind === "expert") {
      const idx = s.target.index;
      s.interventionContents[idx] = (s.interventionContents[idx] || "") + chunk;
      setInterventions((prev) => {
        if (!prev[idx]) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], content: s.interventionContents[idx] };
        return next;
      });
    } else if (s.target.kind === "arbiter") {
      s.arbiterContent = (s.arbiterContent || "") + chunk;
      setArbiter((prev) => ({
        ...prev,
        active: true,
        content: s.arbiterContent,
      }));
    }
  }, []);

  const startDrain = useCallback(() => {
    const s = streamRef.current;
    if (!s.timer) {
      s.timer = setInterval(drainStep, 28); // ~36 ticks/s, fluide
    }
  }, [drainStep]);

  const stopDrain = useCallback(() => {
    const s = streamRef.current;
    // Flush any remaining buffer to target
    if (s.buffer && s.target) {
      const rest = s.buffer;
      s.buffer = "";
      if (s.target.kind === "expert") {
        const idx = s.target.index;
        s.interventionContents[idx] = (s.interventionContents[idx] || "") + rest;
        setInterventions((prev) => {
          if (!prev[idx]) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], content: s.interventionContents[idx] };
          return next;
        });
      } else if (s.target.kind === "arbiter") {
        s.arbiterContent = (s.arbiterContent || "") + rest;
        setArbiter((prev) => ({ ...prev, active: true, content: s.arbiterContent }));
      }
    }
    if (s.timer) {
      clearInterval(s.timer);
      s.timer = null;
    }
  }, []);

  const resetState = () => {
    setInterventions([]);
    setArbiter({ active: false, content: "", done: false });
    setInitialExperts([]);
    setRoundtableId(null);
    streamRef.current.buffer = "";
    streamRef.current.target = null;
    streamRef.current.interventionContents = [];
    streamRef.current.arbiterContent = "";
    if (streamRef.current.timer) {
      clearInterval(streamRef.current.timer);
      streamRef.current.timer = null;
    }
  };

  // Fallback POLLING : si la connexion SSE casse en cours, on poll GET /api/roundtable/{id}
  // jusqu'a ce que la roundtable soit "experts_done" puis on lance la phase 2 arbiter
  // (qui elle-meme a son propre fallback).
  const pollAndRecoverRoundtable = async (rtId) => {
    const token = localStorage.getItem("odys_token");
    const s = streamRef.current;
    const startedAt = Date.now();
    const TIMEOUT_MS = 180000; // 3 min max de polling

    while (Date.now() - startedAt < TIMEOUT_MS) {
      try {
        const res = await fetch(`${API}/roundtable/${rtId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const rt = await res.json();
          // Reconstruit interventions cote UI a partir de la DB
          const dbInterventions = rt.interventions || [];
          if (dbInterventions.length > interventions.length) {
            // Affiche toutes les interventions persistees
            const reconstructed = dbInterventions.map((it) => ({
              agent_id: it.expert_id,
              name: it.expert_name,
              content: it.content,
              streaming: false,
              appel: null,
            }));
            s.interventionContents = dbInterventions.map((it) => it.content);
            setInterventions(reconstructed);
          }

          if (rt.status === "experts_done" || rt.status === "done") {
            // Phase 1 terminee, on passe a phase 2
            if (rt.status === "done" && rt.arbiter) {
              // Tout est deja fait (cas rare : tout s'est passe pendant qu'on pollait)
              setArbiter({ active: true, content: rt.arbiter, done: true });
              setRoundtableId(rtId);
              setRunning(false);
              return;
            }
            // Lance phase 2 arbiter (qui a son propre split, donc OK)
            launchArbiterStream(rtId);
            return;
          }
        }
      } catch (err) {
        console.warn("Polling roundtable echec:", err);
      }
      await new Promise((r) => setTimeout(r, 3000));
    }

    toast.error("La reunion a depasse 3 minutes. Reessaie.");
    setRunning(false);
  };

  // Phase 2 : streame uniquement l'arbiter (apres que les experts aient parle).
  // Utilise une 2eme requete HTTP pour reset le timeout proxy (60s pile en preview).
  const launchArbiterStream = async (rtId) => {
    const token = localStorage.getItem("odys_token");
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const s = streamRef.current;

    // Fallback : si la connexion arbiter casse, on poll jusqu'au verdict final
    const pollArbiter = async () => {
      const startedAt = Date.now();
      const TIMEOUT_MS = 120000;
      while (Date.now() - startedAt < TIMEOUT_MS) {
        try {
          const res = await fetch(`${API}/roundtable/${rtId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const rt = await res.json();
            if (rt.status === "done" && rt.arbiter) {
              setArbiter({ active: true, content: rt.arbiter, done: true });
              setRoundtableId(rtId);
              setRunning(false);
              return;
            }
          }
        } catch (err) {
          console.warn("Polling arbiter echec:", err);
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
      toast.error("L'arbitrage a depasse 2 minutes. Reessaie.");
      setRunning(false);
    };

    // Watchdog pour la phase arbiter (apres que pollArbiter soit defini)
    s.onStall = () => {
      try { ctrl.abort(); } catch {}
      stopDrain();
      stopWatchdog();
      pollArbiter();
    };
    startWatchdog();

    try {
      await fetchEventSource(`${API}/roundtable/${rtId}/arbiter/stream`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({}),
        openWhenHidden: true,
        async onopen(response) {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          let parsed;
          try { parsed = JSON.parse(ev.data); } catch { return; }

          if (ev.event === "arbiter_start") {
            stopDrain();
            s.target = { kind: "arbiter" };
            s.arbiterContent = "";
            setArbiter({ active: true, content: "", done: false });
          } else if (ev.event === "arbiter_token") {
            s.buffer += parsed.text || "";
            pingToken();
            startDrain();
          } else if (ev.event === "arbiter_end") {
            stopDrain();
            pingToken();
            stopWatchdog();
            const canonical = parsed.content && parsed.content.length > 0
              ? parsed.content
              : s.arbiterContent;
            s.arbiterContent = canonical;
            setArbiter({ active: true, content: canonical, done: true });
          } else if (ev.event === "done") {
            setRoundtableId(parsed.roundtable_id);
          } else if (ev.event === "error") {
            toast.error(`Arbitrage interrompu : ${parsed.message || ""}`);
          }
        },
        onclose() {
          stopDrain();
          stopWatchdog();
          // Si le verdict n'est pas done, bascule polling
          setArbiter((prev) => {
            if (prev.done) {
              setRunning(false);
              return prev;
            }
            // Coupure en cours d'arbiter : fallback polling
            console.warn("SSE arbiter coupe avant fin. Bascule polling...");
            pollArbiter();
            return prev;
          });
        },
        onerror(err) {
          stopDrain();
          // Bascule polling au lieu de planter
          console.warn("SSE arbiter erreur. Bascule polling...", err);
          pollArbiter();
          return; // swallow err
        },
      });
    } catch (err) {
      console.warn("Arbiter stream catch, bascule polling:", err);
      stopDrain();
      pollArbiter();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (q.length < 8) {
      toast.error("Pose une vraie question, sois precis.");
      return;
    }
    setRunning(true);
    resetState();

    const token = localStorage.getItem("odys_token");
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    // Flag pour distinguer un abort planifie (transition vers arbiter) d'une vraie erreur
    let transitioningToArbiter = false;
    // ID de la roundtable conserve pour fallback polling en cas de coupure SSE
    let currentRtId = null;

    // Configure le watchdog pour basculer en polling si le stream stale
    streamRef.current.onStall = () => {
      if (currentRtId) {
        transitioningToArbiter = true;
        try { ctrl.abort(); } catch {}
        stopDrain();
        pollAndRecoverRoundtable(currentRtId);
      }
    };
    startWatchdog();

    try {
      await fetchEventSource(`${API}/roundtable/stream`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ question: q }),
        openWhenHidden: true,
        async onopen(response) {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          let parsed;
          try { parsed = JSON.parse(ev.data); } catch { return; }
          const s = streamRef.current;

          if (ev.event === "meta") {
            currentRtId = parsed.roundtable_id;
            setRoundtableId(parsed.roundtable_id);
            setInitialExperts(parsed.initial_experts || []);
          } else if (ev.event === "expert_start") {
            // Flush current buffer (au cas ou il restait quelque chose d'un expert precedent)
            stopDrain();
            const newIdx = s.interventionContents.length;
            s.interventionContents.push("");
            s.target = { kind: "expert", index: newIdx };
            setInterventions((prev) => [
              ...prev,
              {
                agent_id: parsed.agent_id,
                name: parsed.name,
                content: "",
                streaming: true,
              },
            ]);
          } else if (ev.event === "token") {
            s.buffer += parsed.text || "";
            pingToken();
            startDrain();
          } else if (ev.event === "expert_end") {
            // Flush + replace by canonical content from backend (sans APPEL_EXPERT)
            stopDrain();
            pingToken();
            const idx = s.target?.index;
            const canonical = parsed.content || (idx != null ? s.interventionContents[idx] : "");
            if (idx != null) {
              s.interventionContents[idx] = canonical;
            }
            const appel = parsed.appel || null;
            setInterventions((prev) => {
              if (idx == null || !prev[idx]) return prev;
              const next = [...prev];
              next[idx] = {
                ...next[idx],
                content: canonical,
                streaming: false,
                appel,
              };
              return next;
            });
          } else if (ev.event === "arbiter_start") {
            // Switch target to arbiter
            stopDrain();
            s.target = { kind: "arbiter" };
            s.arbiterContent = "";
            setArbiter({ active: true, content: "", done: false });
          } else if (ev.event === "arbiter_token") {
            s.buffer += parsed.text || "";
            pingToken();
            startDrain();
          } else if (ev.event === "arbiter_end") {
            stopDrain();
            pingToken();
            const canonical = parsed.content && parsed.content.length > 0
              ? parsed.content
              : s.arbiterContent;
            s.arbiterContent = canonical;
            setArbiter({ active: true, content: canonical, done: true });
          } else if (ev.event === "experts_done") {
            // Phase 1 (experts) terminee. On lance la phase 2 (arbiter) dans une 2eme requete
            // pour respecter les timeouts proxy (60s en preview, parfois moins en prod).
            const rtId = parsed.roundtable_id;
            setRoundtableId(rtId);
            transitioningToArbiter = true;
            stopWatchdog();
            // Lance phase 2 AVANT d'aborter, pour eviter race condition
            launchArbiterStream(rtId);
            ctrl.abort();
          } else if (ev.event === "done") {
            setRoundtableId(parsed.roundtable_id);
          }
        },
        onclose() {
          stopDrain();
          // Ne PAS couper le running si on transitionne vers l'arbiter (phase 2)
          if (!transitioningToArbiter) {
            stopWatchdog();
            // Coupure inattendue de la phase 1 : si on a un roundtable_id, on tente le fallback polling
            if (currentRtId) {
              console.warn("SSE phase 1 coupe avant experts_done. Bascule en mode polling...");
              transitioningToArbiter = true; // empeche les autres handlers de setRunning(false)
              pollAndRecoverRoundtable(currentRtId);
            } else {
              setRunning(false);
            }
          }
        },
        onerror(err) {
          stopDrain();
          // Si abort intentionnel pour passer a phase 2 : swallow
          if (transitioningToArbiter) {
            return;
          }
          // Si on a deja un roundtable_id (au moins meta recu), bascule polling
          if (currentRtId) {
            console.warn("SSE phase 1 erreur. Bascule en mode polling...", err);
            transitioningToArbiter = true;
            pollAndRecoverRoundtable(currentRtId);
            return; // swallow err
          }
          setRunning(false);
          throw err;
        },
      });
    } catch (err) {
      // Si on est en transition vers l'arbiter, l'erreur d'abort est attendue : on swallow
      if (transitioningToArbiter) {
        console.log("Phase 1 abort intentionnel ou bascule polling - non bloquant");
        return;
      }
      // Si on a un roundtable_id deja, bascule polling au lieu d'echouer
      if (currentRtId) {
        console.warn("Phase 1 catch erreur, bascule polling:", err);
        pollAndRecoverRoundtable(currentRtId);
        return;
      }
      console.error("Roundtable stream error:", err);
      toast.error("La Reunion d'equipage a ete interrompue. Reessaie.");
      stopDrain();
      setRunning(false);
    }
  };

  const handleStop = () => {
    if (ctrlRef.current) {
      try { ctrlRef.current.abort(); } catch {}
    }
    stopDrain();
    stopWatchdog();
    setRunning(false);
  };

  const handleRefine = async (refinements) => {
    if (!roundtableId || refining) return;
    setRefining(true);
    setRefinedVerdict({ active: true, content: "", done: false });

    const token = localStorage.getItem("odys_token");
    const ctrl = new AbortController();
    let acc = "";

    try {
      await fetchEventSource(`${API}/roundtable/${roundtableId}/refine`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ refinements }),
        openWhenHidden: true,
        async onopen(response) {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          let parsed;
          try { parsed = JSON.parse(ev.data); } catch { return; }

          if (ev.event === "refined_start") {
            setRefinedVerdict({ active: true, content: "", done: false });
          } else if (ev.event === "refined_token") {
            acc += parsed.text || "";
            setRefinedVerdict((prev) => ({
              ...prev,
              active: true,
              content: acc,
            }));
          } else if (ev.event === "refined_end") {
            const canonical = parsed.content && parsed.content.length > 0 ? parsed.content : acc;
            acc = canonical;
            setRefinedVerdict({ active: true, content: canonical, done: true });
          } else if (ev.event === "done") {
            setRefining(false);
          }
        },
        onclose() { setRefining(false); },
        onerror(err) {
          setRefining(false);
          throw err;
        },
      });
    } catch (err) {
      console.error("Refine error:", err);
      toast.error("Le verdict affine n'a pas pu etre genere. Reessaie.");
      setRefining(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!roundtableId) return;
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("odys_token");
      const res = await fetch(`${API}/roundtable/${roundtableId}/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compte-rendu-equipage-odys-${roundtableId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Compte-rendu d'equipage telecharge.");
    } catch (err) {
      console.error(err);
      toast.error("Impossible de generer le PDF pour le moment.");
    } finally {
      setPdfLoading(false);
    }
  };

  const canPdf = !running && arbiter.done && !!roundtableId;

  return (
    <div className="min-h-screen tr-page">
      {/* Header */}
      <header className="tr-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard-btn"
            className="flex items-center gap-2 text-sm font-medium tr-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </button>
          <div className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[color:var(--gold)]">
            <Anchor className="w-3.5 h-3.5" />
            <span>Reunion d'equipage</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 pb-16 pt-2">
        {/* Title block */}
        <section className="tr-title-block">
          <div className="tr-eyebrow">Comite patrimonial Odys'IA</div>
          <h1 className="tr-title">Reunion d'equipage</h1>
          <p className="tr-subtitle">
            Ton equipage de capitaines IA se reunit autour de la carte. Ils
            debattent, se completent, parfois se contredisent — puis ODYS'GUIDE
            tranche et pose le cap. Un seul ordre du jour : ta question.
          </p>
          <div className="tr-divider" />
        </section>

        {/* Question form */}
        {!running && interventions.length === 0 && !arbiter.content && (
          <section className="tr-form-section">
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="tr-form-label">
                <Scroll className="w-4 h-4 inline-block mr-2 -mt-0.5" />
                Ordre du jour
              </label>
              <Textarea
                data-testid="roundtable-question-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Decris ta situation et ta question en quelques phrases. Plus c'est precis, plus l'arbitrage sera utile..."
                className="tr-textarea"
                rows={5}
              />

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.22em] text-[color:var(--muted)] font-semibold">
                  Exemples de questions
                </p>
                <div className="flex flex-wrap gap-2">
                  {SAMPLE_QUESTIONS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setQuestion(s)}
                      data-testid={`sample-question-${i}`}
                      className="tr-chip"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  data-testid="convene-roundtable-btn"
                  className="tr-cta"
                  disabled={running || question.trim().length < 8}
                >
                  <Sailboat className="w-4 h-4 mr-2" />
                  Convoquer l'equipage
                </Button>
              </div>
            </form>
          </section>
        )}

        {/* Live debate */}
        {(running || interventions.length > 0 || arbiter.content) && (
          <section className="tr-debate-section">
            {/* Recap question */}
            <div className="tr-question-card">
              <div className="tr-eyebrow-sm">Ordre du jour</div>
              <p className="tr-question-text">{question}</p>
              {initialExperts.length > 0 && (
                <div className="tr-experts-row">
                  <Users className="w-3.5 h-3.5 text-[color:var(--gold)]" />
                  <span className="text-xs uppercase tracking-[0.18em] text-[color:var(--muted)]">
                    Equipage convoque :
                  </span>
                  {initialExperts.map((e) => (
                    <Badge
                      key={e}
                      data-testid={`initial-expert-${e}`}
                      className="tr-expert-badge"
                    >
                      {EXPERT_LABEL[e] || e.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Interventions */}
            <div className="space-y-6 mt-6">
              {interventions.map((it, idx) => {
                const avatar = AGENT_AVATARS[it.agent_id];
                return (
                  <Card
                    key={`int-${idx}-${it.agent_id}`}
                    className="tr-intervention-card"
                    data-testid={`intervention-${idx}`}
                  >
                    <CardContent className="p-0">
                      <div className="tr-intervention-header">
                        <div className="tr-intervention-meta">
                          <div className="tr-intervention-idx">
                            Intervention {String(idx + 1).padStart(2, "0")}
                          </div>
                          <div className="tr-intervention-name">{it.name}</div>
                        </div>
                        {avatar && (
                          <img
                            src={avatar}
                            alt={it.name}
                            className="tr-intervention-avatar"
                          />
                        )}
                      </div>
                      <div className="tr-intervention-body">
                        {renderContent(stripFollowup(it.content))}
                        {it.streaming && (
                          <span className="tr-cursor" aria-hidden="true" />
                        )}
                      </div>
                      {it.appel && !it.streaming && (
                        <div className="tr-relay">
                          <Compass className="w-3.5 h-3.5" />
                          Relais demande a{" "}
                          <strong>
                            {EXPERT_LABEL[it.appel] || it.appel.toUpperCase()}
                          </strong>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Arbiter */}
            {arbiter.active && (() => {
              const displayContent = stripFollowup(arbiter.content);
              const verdictQuestions = arbiter.done ? parseFollowup(arbiter.content) : [];
              const hasRefinedAlready = refinedVerdict.active;
              return (
              <>
              <div className="tr-arbiter-block" data-testid="arbiter-block">
                <div className="tr-arbiter-header">
                  <div className="tr-eyebrow">
                    {hasRefinedAlready ? "Verdict initial" : "Arbitrage du capitaine"}
                  </div>
                  <h2 className="tr-arbiter-title">Verdict ODYS'GUIDE</h2>
                  <div className="tr-divider" />
                  <div className="mt-2">
                    <AiGeneratedBadge label="Verdict genere par IA" size="xs" />
                  </div>
                </div>

                {/* CTA AU-DESSUS du verdict (avant le corps) - pour affiner le verdict */}
                {verdictQuestions.length > 0 && !hasRefinedAlready && (
                  <button
                    onClick={() => setFollowUpOpen(true)}
                    data-testid="verdict-followup-cta"
                    className="mb-5 mt-2 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md group transition-all text-left"
                    style={{
                      background: "linear-gradient(180deg, #fff, var(--paper))",
                      border: "1px solid var(--ink)",
                      borderLeft: "4px solid var(--gold)",
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: "var(--ink)" }}
                      >
                        <MessageSquareText className="w-4 h-4 text-white" strokeWidth={1.8} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[0.62rem] uppercase font-semibold" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
                          {verdictQuestions.length} question{verdictQuestions.length > 1 ? "s" : ""} pour aller plus loin
                        </span>
                        <span className="block text-sm font-semibold" style={{ color: "var(--ink)", fontFamily: "var(--serif)" }}>
                          Affiner ce verdict avec tes precisions
                        </span>
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: "var(--gold)" }} />
                  </button>
                )}

                <div className="tr-arbiter-body">
                  {renderContent(displayContent)}
                  {!arbiter.done && (
                    <span className="tr-cursor" aria-hidden="true" />
                  )}
                </div>
              </div>

              {/* Verdict affine V2 (apres reponses FOLLOWUP) */}
              {refinedVerdict.active && (
                <div className="tr-arbiter-block tr-refined-block" data-testid="refined-verdict-block">
                  <div className="tr-arbiter-header">
                    <div className="tr-eyebrow">Verdict affine</div>
                    <h2 className="tr-arbiter-title">Verdict ODYS'GUIDE — V2</h2>
                    <div className="tr-divider" />
                    <p className="tr-refined-sub">
                      ODYS'GUIDE a affine son arbitrage avec tes precisions.
                    </p>
                  </div>
                  <div className="tr-arbiter-body">
                    {renderContent(stripFollowup(refinedVerdict.content))}
                    {!refinedVerdict.done && (
                      <span className="tr-cursor" aria-hidden="true" />
                    )}
                  </div>
                </div>
              )}
              </>
              );
            })()}

            {/* Bottom actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-10">
              <div className="flex items-center gap-2 text-xs text-[color:var(--muted)]">
                {running ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    L'equipage est en seance...
                  </>
                ) : (
                  <>
                    <Ship className="w-3.5 h-3.5 text-[color:var(--teal)]" />
                    Seance close. Ton compte-rendu est pret.
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {running ? (
                  <Button
                    onClick={handleStop}
                    data-testid="stop-roundtable-btn"
                    variant="outline"
                    className="tr-secondary-btn"
                  >
                    Interrompre la seance
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        resetState();
                        setQuestion("");
                      }}
                      data-testid="new-roundtable-btn"
                      variant="outline"
                      className="tr-secondary-btn"
                    >
                      Nouvelle reunion
                    </Button>
                    {roundtableId && (
                      <FavoriteButton
                        kind="roundtable"
                        targetId={roundtableId}
                        agentType="roundtable"
                        variant="full"
                        label="Favori"
                        summary={(question || "").slice(0, 200)}
                      />
                    )}
                    <Button
                      onClick={handleDownloadPdf}
                      data-testid="download-roundtable-pdf-btn"
                      disabled={!canPdf || pdfLoading}
                      className="tr-cta"
                    >
                      {pdfLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Telecharger le compte-rendu
                    </Button>
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        <div ref={debateEndRef} />
      </main>

      {/* Bottom-sheet pour repondre aux questions du verdict ODYS'GUIDE.
          Les reponses sont injectees a ODYS'GUIDE qui re-genere un verdict V2 affine
          en bas de la page (in-place, sans navigation). */}
      <FollowUpSheet
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        questions={arbiter.done ? parseFollowup(arbiter.content) : []}
        agentName="ODYS'GUIDE"
        onSubmit={(text) => {
          handleRefine(text);
        }}
      />

      <style>{`
        .tr-page {
          background:
            radial-gradient(1200px 600px at 80% -10%, #fff8ec 0%, transparent 55%),
            var(--paper-2);
          color: var(--ink);
        }
        .tr-header {
          border-bottom: 1px solid var(--line);
          background: rgba(250, 247, 241, 0.85);
          backdrop-filter: blur(8px);
          position: sticky;
          top: 0;
          z-index: 20;
        }
        .tr-back { color: var(--ink); opacity: 0.8; transition: opacity 0.2s; }
        .tr-back:hover { opacity: 1; }

        .tr-title-block { padding-top: 56px; padding-bottom: 24px; }
        .tr-eyebrow {
          font-family: var(--sans);
          text-transform: uppercase;
          letter-spacing: 0.28em;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--gold);
        }
        .tr-eyebrow-sm {
          font-family: var(--sans);
          text-transform: uppercase;
          letter-spacing: 0.22em;
          font-size: 0.62rem;
          font-weight: 700;
          color: var(--gold);
          margin-bottom: 0.5rem;
        }
        .tr-title {
          font-family: var(--serif);
          font-weight: 600;
          font-size: clamp(2.4rem, 5vw, 3.6rem);
          color: var(--ink);
          letter-spacing: -0.01em;
          line-height: 1.05;
          margin: 0.5rem 0 0.75rem;
        }
        .tr-subtitle {
          font-family: var(--sans);
          color: var(--ink-soft);
          max-width: 62ch;
          font-size: 1.02rem;
          line-height: 1.6;
        }
        .tr-divider {
          width: 56px;
          height: 2.5px;
          background: var(--gold);
          margin: 18px 0 4px;
          border-radius: 2px;
        }

        .tr-form-section {
          margin-top: 24px;
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: 6px;
          padding: 28px 28px 24px;
          box-shadow: var(--shadow);
          position: relative;
          overflow: hidden;
        }
        .tr-form-section::before {
          content: "";
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 4px;
          background: var(--gold);
        }
        .tr-form-label {
          font-family: var(--sans);
          font-weight: 600;
          font-size: 0.72rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--ink);
          display: block;
        }
        .tr-textarea {
          background: #fff !important;
          border: 1px solid var(--line) !important;
          border-radius: 4px;
          font-family: var(--sans);
          font-size: 0.98rem;
          line-height: 1.55;
          color: var(--ink);
          padding: 14px 16px;
          resize: vertical;
          min-height: 140px;
          -webkit-text-fill-color: var(--ink);
        }
        .tr-textarea:focus {
          outline: none;
          border-color: var(--gold) !important;
          box-shadow: 0 0 0 3px rgba(181, 137, 74, 0.12);
        }
        .tr-chip {
          background: var(--paper-2);
          border: 1px solid var(--line);
          color: var(--ink-soft);
          font-family: var(--sans);
          font-size: 0.78rem;
          padding: 0.55rem 0.95rem;
          border-radius: 9999px;
          transition: 0.2s;
          cursor: pointer;
          text-align: left;
          line-height: 1.3;
          max-width: 100%;
        }
        .tr-chip:hover {
          border-color: var(--gold);
          background: var(--gold-bg);
          color: var(--ink);
        }

        .tr-cta {
          background: var(--ink) !important;
          color: #fff !important;
          font-family: var(--sans);
          font-weight: 600;
          padding: 0 1.4rem;
          height: 46px;
          border-radius: 4px;
          border: 1px solid var(--ink);
          letter-spacing: 0.02em;
          position: relative;
          overflow: hidden;
        }
        .tr-cta::after {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 2px;
          background: var(--gold);
        }
        .tr-cta:hover { background: #0d1729 !important; }
        .tr-cta:disabled { opacity: 0.55; cursor: not-allowed; }

        .tr-secondary-btn {
          background: #fff !important;
          color: var(--ink) !important;
          border: 1px solid rgba(22, 35, 59, 0.18) !important;
          font-family: var(--sans);
          font-weight: 600;
          height: 46px;
          padding: 0 1.2rem;
          border-radius: 4px;
        }
        .tr-secondary-btn:hover { background: var(--paper-2) !important; }

        .tr-debate-section { margin-top: 20px; }
        .tr-question-card {
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: 6px;
          padding: 22px 26px;
          box-shadow: var(--shadow-soft);
          position: relative;
        }
        .tr-question-text {
          font-family: var(--serif);
          font-size: 1.15rem;
          line-height: 1.45;
          color: var(--ink);
          margin: 0.4rem 0 0.9rem;
        }
        .tr-experts-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.4rem 0.6rem;
          padding-top: 0.4rem;
          border-top: 1px solid var(--line);
          margin-top: 0.6rem;
        }
        .tr-expert-badge {
          background: var(--gold-bg) !important;
          color: var(--gold-dark) !important;
          border: 1px solid var(--gold-soft) !important;
          font-family: var(--sans);
          font-weight: 600;
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          border-radius: 9999px;
          padding: 0.18rem 0.6rem;
        }

        .tr-intervention-card {
          background: var(--paper) !important;
          border: 1px solid var(--line) !important;
          border-radius: 6px;
          box-shadow: var(--shadow-soft);
          overflow: hidden;
        }
        .tr-intervention-header {
          background: var(--gold-bg);
          border-bottom: 1px solid var(--gold-soft);
          border-top: 2px solid var(--gold);
          padding: 14px 22px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .tr-intervention-meta { display: flex; flex-direction: column; gap: 2px; }
        .tr-intervention-idx {
          font-family: var(--sans);
          text-transform: uppercase;
          letter-spacing: 0.22em;
          font-size: 0.62rem;
          font-weight: 700;
          color: var(--gold);
        }
        .tr-intervention-name {
          font-family: var(--serif);
          font-weight: 600;
          font-size: 1.15rem;
          color: var(--ink);
        }
        .tr-intervention-avatar {
          width: 42px;
          height: 42px;
          border-radius: 8px;
          object-fit: cover;
          border: 1.5px solid var(--gold);
          flex: none;
        }
        .tr-intervention-body {
          padding: 18px 24px 22px;
          font-family: var(--sans);
          font-size: 0.98rem;
          line-height: 1.62;
          color: var(--ink);
        }
        .tr-paragraph { margin: 0 0 0.85rem; }
        .tr-bullet {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin: 0.35rem 0;
          padding-left: 2px;
        }
        .tr-bullet-dot {
          width: 6px;
          height: 6px;
          background: var(--gold);
          border-radius: 9999px;
          margin-top: 0.65rem;
          flex: none;
        }
        .tr-numbered {
          display: flex;
          gap: 10px;
          margin: 0.35rem 0;
        }
        .tr-numbered-idx {
          font-family: var(--serif);
          font-weight: 600;
          color: var(--gold);
          min-width: 1.4rem;
        }
        .tr-section-label {
          font-family: var(--sans);
          font-weight: 700;
          font-size: 0.7rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--gold);
          margin: 1.2rem 0 0.4rem;
        }
        .tr-section-label:first-child { margin-top: 0; }

        .tr-relay {
          margin: 0 22px 18px;
          font-family: var(--sans);
          font-size: 0.8rem;
          color: var(--teal-dark);
          background: var(--teal-soft);
          border: 1px dashed var(--teal);
          border-radius: 4px;
          padding: 0.55rem 0.85rem;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }

        .tr-cursor {
          display: inline-block;
          width: 7px;
          height: 1.05rem;
          background: var(--ink);
          margin-left: 2px;
          vertical-align: -2px;
          animation: tr-blink 0.9s steps(2) infinite;
        }
        @keyframes tr-blink { 50% { opacity: 0; } }

        .tr-arbiter-block {
          margin-top: 34px;
          background: linear-gradient(180deg, #fff, var(--gold-bg));
          border: 1px solid var(--gold-soft);
          border-top: 3px solid var(--gold);
          border-radius: 6px;
          padding: 30px 32px 28px;
          box-shadow: var(--shadow);
          position: relative;
        }
        .tr-arbiter-header { margin-bottom: 0.5rem; }
        .tr-arbiter-title {
          font-family: var(--serif);
          font-weight: 600;
          font-size: clamp(1.7rem, 3vw, 2.2rem);
          color: var(--ink);
          margin: 0.4rem 0 0;
          letter-spacing: -0.01em;
        }
        .tr-arbiter-body {
          margin-top: 1.1rem;
          font-family: var(--sans);
          font-size: 1rem;
          line-height: 1.62;
          color: var(--ink);
          min-height: 1.5rem;
        }

        .tr-refined-block {
          margin-top: 24px;
          background: linear-gradient(180deg, var(--ink) 0%, #0d1729 100%) !important;
          border: 1px solid var(--gold-soft) !important;
          border-top: 3px solid var(--gold) !important;
          color: #FAF7F1 !important;
          position: relative;
        }
        .tr-refined-block .tr-eyebrow { color: var(--gold); }
        .tr-refined-block .tr-arbiter-title { color: #FAF7F1; }
        .tr-refined-block .tr-arbiter-body { color: #E9E4DA; }
        .tr-refined-block .tr-paragraph,
        .tr-refined-block .tr-bullet,
        .tr-refined-block .tr-numbered { color: #E9E4DA; }
        .tr-refined-block .tr-bullet-dot { background: var(--gold); }
        .tr-refined-block .tr-numbered-idx { color: var(--gold); }
        .tr-refined-block .tr-section-label { color: var(--gold); }
        .tr-refined-block .tr-cursor { background: var(--gold); }
        .tr-refined-sub {
          margin-top: 0.6rem;
          font-family: var(--sans);
          font-size: 0.82rem;
          color: #C8CFDC;
          font-style: italic;
        }

        @media (max-width: 640px) {
          .tr-form-section, .tr-question-card, .tr-arbiter-block { padding: 20px; }
          .tr-intervention-body { padding: 16px 18px 20px; }
          .tr-intervention-header { padding: 12px 16px; }
        }
      `}</style>
    </div>
  );
}
